/**
 * useFileManagement Hook
 * Main hook for file management operations
 */

import { useState, useCallback, useEffect } from 'react';
import { useFileService } from '@actschurch/ccms-sdk';
import {
  FileRecordModelView,
  FileFilters,
  SortOptions,
  PaginationOptions,
  ViewMode,
  SelectedFilesState,
  FolderNavigationState,
  BreadcrumbSegment,
} from '../types';
import { toast } from '@/components/ui/providers/AlertProvider';

export interface UseFileManagementReturn {
  // State
  files: FileRecordModelView[];
  loading: boolean;
  error: string | null;
  filters: FileFilters;
  sort: SortOptions;
  pagination: PaginationOptions;
  viewMode: ViewMode;
  selectedFiles: SelectedFilesState;
  navigation: FolderNavigationState;

  // Actions
  fetchFiles: () => Promise<void>;
  navigateToFolder: (path: string) => void;
  navigateBack: () => void;
  navigateForward: () => void;
  searchFiles: (query: string) => void;
  applyFilters: (newFilters: Partial<FileFilters>) => void;
  setSortOptions: (newSort: SortOptions) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  toggleViewMode: () => void;
  selectFile: (fileId: string) => void;
  deselectFile: (fileId: string) => void;
  selectAllFiles: () => void;
  deselectAllFiles: () => void;
  deleteFile: (fileId: string) => Promise<void>;
  bulkDelete: (fileIds: string[]) => Promise<void>;
  moveFile: (fileId: string, newFolderPath: string) => Promise<void>;
  bulkMove: (fileIds: string[], newFolderPath: string) => Promise<void>;
  refreshFiles: () => Promise<void>;
}

export const useFileManagement = (
  initialFolderPath: string = '/',
  domain?: string,
  recordId?: string
): UseFileManagementReturn => {
  const fileService = useFileService();

  // State
  const [files, setFiles] = useState<FileRecordModelView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FileFilters>({
    folderPath: initialFolderPath,
    recursive: false,
    domains: domain ? [domain as any] : undefined,
  });
  const [sort, setSort] = useState<SortOptions>({
    field: 'createdAt',
    direction: 'desc',
  });
  const [pagination, setPagination] = useState<PaginationOptions>({
    page: 1,
    pageSize: 50,
    total: 0,
  });
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedFiles, setSelectedFiles] = useState<SelectedFilesState>({
    fileIds: new Set(),
    files: [],
  });
  const [navigation, setNavigation] = useState<FolderNavigationState>({
    currentPath: initialFolderPath,
    breadcrumbs: [{ name: 'Home', path: '/' }],
    history: [initialFolderPath],
    historyIndex: 0,
  });

  // Generate breadcrumbs from path
  const generateBreadcrumbs = useCallback((path: string): BreadcrumbSegment[] => {
    if (path === '/') {
      return [{ name: 'Home', path: '/' }];
    }

    const segments = path.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbSegment[] = [{ name: 'Home', path: '/' }];

    let currentPath = '';
    segments.forEach((segment) => {
      currentPath += `/${segment}`;
      breadcrumbs.push({ name: segment, path: currentPath });
    });

    return breadcrumbs;
  }, []);

  // Fetch files
  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const searchRequest = {
        searchQuery: filters.search,
        domains: filters.domains,
        fileTypes: filters.fileTypes,
        folderPath: filters.folderPath || navigation.currentPath,
        recursive: filters.recursive,
        dateFrom: filters.dateFrom?.toISOString(),
        dateTo: filters.dateTo?.toISOString(),
        uploadedBy: filters.uploadedBy,
        statuses: filters.statuses,
        minSize: filters.minSize,
        maxSize: filters.maxSize,
        pageNumber: pagination.page,
        pageSize: pagination.pageSize,
      };

      const result = await fileService.search(searchRequest);

      // Client-side sorting
      const sorted = [...result].sort((a, b) => {
        const aValue = a[sort.field];
        const bValue = b[sort.field];

        if (aValue === bValue) return 0;

        const comparison = aValue > bValue ? 1 : -1;
        return sort.direction === 'asc' ? comparison : -comparison;
      });

      setFiles(sorted);
      setPagination((prev) => ({ ...prev, total: sorted.length }));
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch files';
      setError(errorMessage);
      toast.error('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fileService, filters, navigation.currentPath, pagination.page, pagination.pageSize, sort]);

  // Navigate to folder
  const navigateToFolder = useCallback(
    (path: string) => {
      const newBreadcrumbs = generateBreadcrumbs(path);
      const newHistory = [...navigation.history.slice(0, navigation.historyIndex + 1), path];

      setNavigation({
        currentPath: path,
        breadcrumbs: newBreadcrumbs,
        history: newHistory,
        historyIndex: newHistory.length - 1,
      });

      setFilters((prev) => ({ ...prev, folderPath: path }));
      deselectAllFiles();
    },
    [navigation.history, navigation.historyIndex, generateBreadcrumbs]
  );

  // Navigate back
  const navigateBack = useCallback(() => {
    if (navigation.historyIndex > 0) {
      const newIndex = navigation.historyIndex - 1;
      const newPath = navigation.history[newIndex];

      setNavigation((prev) => ({
        ...prev,
        currentPath: newPath,
        breadcrumbs: generateBreadcrumbs(newPath),
        historyIndex: newIndex,
      }));

      setFilters((prev) => ({ ...prev, folderPath: newPath }));
    }
  }, [navigation.history, navigation.historyIndex, generateBreadcrumbs]);

  // Navigate forward
  const navigateForward = useCallback(() => {
    if (navigation.historyIndex < navigation.history.length - 1) {
      const newIndex = navigation.historyIndex + 1;
      const newPath = navigation.history[newIndex];

      setNavigation((prev) => ({
        ...prev,
        currentPath: newPath,
        breadcrumbs: generateBreadcrumbs(newPath),
        historyIndex: newIndex,
      }));

      setFilters((prev) => ({ ...prev, folderPath: newPath }));
    }
  }, [navigation.history, navigation.historyIndex, generateBreadcrumbs]);

  // Search files
  const searchFiles = useCallback((query: string) => {
    setFilters((prev) => ({ ...prev, search: query }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Apply filters
  const applyFilters = useCallback((newFilters: Partial<FileFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Set sort options
  const setSortOptions = useCallback((newSort: SortOptions) => {
    setSort(newSort);
  }, []);

  // Set page
  const setPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // Set page size
  const setPageSize = useCallback((pageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // Toggle view mode
  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === 'grid' ? 'table' : 'grid'));
  }, []);

  // Select file
  const selectFile = useCallback(
    (fileId: string) => {
      const file = files.find((f) => f.id === fileId);
      if (!file) return;

      setSelectedFiles((prev) => {
        const newFileIds = new Set(prev.fileIds);
        newFileIds.add(fileId);

        return {
          fileIds: newFileIds,
          files: [...prev.files, file],
        };
      });
    },
    [files]
  );

  // Deselect file
  const deselectFile = useCallback((fileId: string) => {
    setSelectedFiles((prev) => {
      const newFileIds = new Set(prev.fileIds);
      newFileIds.delete(fileId);

      return {
        fileIds: newFileIds,
        files: prev.files.filter((f) => f.id !== fileId),
      };
    });
  }, []);

  // Select all files
  const selectAllFiles = useCallback(() => {
    setSelectedFiles({
      fileIds: new Set(files.map((f) => f.id)),
      files: [...files],
    });
  }, [files]);

  // Deselect all files
  const deselectAllFiles = useCallback(() => {
    setSelectedFiles({
      fileIds: new Set(),
      files: [],
    });
  }, []);

  // Delete single file
  const deleteFile = useCallback(
    async (fileId: string) => {
      try {
        await fileService.delete(fileId);
        toast.success('Success', 'File deleted successfully');
        await fetchFiles();
        deselectFile(fileId);
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to delete file');
      }
    },
    [fileService, fetchFiles, deselectFile]
  );

  // Bulk delete files
  const bulkDelete = useCallback(
    async (fileIds: string[]) => {
      try {
        const response = await fileService.bulkDelete({ fileIds });

        if (response.allSuccessful) {
          toast.success('Success', `${response.successCount} file(s) deleted successfully`);
        } else {
          toast.warning('Partial Success', `${response.successCount} file(s) deleted, ${response.failureCount} failed`);
        }

        await fetchFiles();
        deselectAllFiles();
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to delete files');
      }
    },
    [fileService, fetchFiles, deselectAllFiles]
  );

  // Move single file
  const moveFile = useCallback(
    async (fileId: string, newFolderPath: string) => {
      try {
        await fileService.moveToFolder(fileId, { newFolderPath });
        toast.success('Success', 'File moved successfully');
        await fetchFiles();
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to move file');
      }
    },
    [fileService, fetchFiles]
  );

  // Bulk move files
  const bulkMove = useCallback(
    async (fileIds: string[], newFolderPath: string) => {
      try {
        await Promise.all(
          fileIds.map((fileId) => fileService.moveToFolder(fileId, { newFolderPath }))
        );

        toast.success('Success', `${fileIds.length} file(s) moved successfully`);

        await fetchFiles();
        deselectAllFiles();
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to move files');
      }
    },
    [fileService, fetchFiles, deselectAllFiles]
  );

  // Refresh files
  const refreshFiles = useCallback(async () => {
    await fetchFiles();
  }, [fetchFiles]);

  // Fetch files on mount and when dependencies change
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  return {
    // State
    files,
    loading,
    error,
    filters,
    sort,
    pagination,
    viewMode,
    selectedFiles,
    navigation,

    // Actions
    fetchFiles,
    navigateToFolder,
    navigateBack,
    navigateForward,
    searchFiles,
    applyFilters,
    setSortOptions,
    setPage,
    setPageSize,
    toggleViewMode,
    selectFile,
    deselectFile,
    selectAllFiles,
    deselectAllFiles,
    deleteFile,
    bulkDelete,
    moveFile,
    bulkMove,
    refreshFiles,
  };
};
